import { useRef, useState } from "react";
import ToggleSwitch from "../BaseComponents/Toggle/ToggleSwitch";
import styles from "./FooterNavigation.module.css";
import { Plus, Trash2, ChevronUp, ChevronDown, FileUp } from "lucide-react";
import { useUploadFooterDocument } from "../../api/layout/useFooterConfigApi";
import { useToast } from "../../context/ToastContext";

const slugify = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Tidy a footer link URL without changing what it means.
 *
 * A saved link read "//p/Bankgegevens-#Bankgegevens" and was dead on the site:
 * a browser reads a leading double slash as the start of a HOST, so it went
 * looking for a site called "p" instead of a page on Sunsky. The extra slash is
 * the kind of thing a stray keystroke leaves behind and nothing here caught it.
 *
 * Only unambiguous repairs are made. A real external link (https://…, mailto:,
 * tel:) is left exactly as typed, and so is an anchor. The path itself is never
 * rewritten: a wrong slug is the author's to fix, and silently "correcting" it
 * would hide the mistake rather than surface it.
 */
export const tidyLinkUrl = (raw = "") => {
  const url = String(raw ?? "").trim();
  if (!url) return "";
  // Anything with a scheme, or a plain anchor, is left alone.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith("#")) return url;
  // Collapse the leading slashes that turn a path into a host.
  return url.replace(/^\/{2,}/, "/");
};

/**
 * What is wrong with this URL, in words, or null when nothing is. Shown next to
 * the field rather than blocking the save: the author may be part way through
 * typing, and a footer link is not worth refusing a whole page of edits over.
 */
export const linkUrlProblem = (raw = "") => {
  const url = String(raw ?? "").trim();
  if (!url) return "Empty, so this link goes nowhere.";
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith("#")) return null;
  if (/^\/{2,}/.test(url)) return "Starts with two slashes, which sends visitors off the site.";
  if (!url.startsWith("/")) return "Should start with / for a page on this site, or https:// for another one.";
  if (/\s/.test(url)) return "Contains a space, which will break the link.";
  return null;
};

const MoveControl = ({ onMoveUp, onMoveDown, disableUp, disableDown, upTitle, downTitle }) => (
  <div className={styles.moveControl}>
    <button
      type="button"
      className={styles.moveBtn}
      onClick={onMoveUp}
      disabled={disableUp}
      title={upTitle}
    >
      <ChevronUp size={13} />
    </button>
    <button
      type="button"
      className={styles.moveBtn}
      onClick={onMoveDown}
      disabled={disableDown}
      title={downTitle}
    >
      <ChevronDown size={13} />
    </button>
  </div>
);

const FooterNavigation = ({ data, setData }) => {
  const categories = Array.isArray(data) ? data : [];
  const { showToast } = useToast();
  // Terms, traveller rights and the rest are published as PDFs. A link could
  // only ever point at a typed URL, so the file had to be hosted elsewhere
  // first; now it can be uploaded here and the link points straight at it.
  const { uploadDocument } = useUploadFooterDocument();
  // One hidden input per link, keyed by category and row, so a click opens the
  // picker for that link and no other.
  const fileInputs = useRef({});
  const [uploadingKey, setUploadingKey] = useState(null);

  const handleDocument = async (categoryIndex, linkIndex, file) => {
    if (!file) return;
    const key = `${categoryIndex}-${linkIndex}`;

    if (!/\.pdf$/i.test(file.name) || file.type !== "application/pdf") {
      showToast("Choose a PDF file", "error");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      showToast("That PDF is over 15 MB. Please use a smaller file.", "error");
      return;
    }

    setUploadingKey(key);
    try {
      const result = await uploadDocument(file);
      if (!result?.documentUrl) throw new Error("No document URL returned");
      updateLink(categoryIndex, linkIndex, "url", result.documentUrl);
      showToast("PDF uploaded. Save the footer to publish the link.", "success");
    } catch (err) {
      showToast(
        err?.response?.data?.message || err?.message || "Could not upload the PDF",
        "error"
      );
    } finally {
      setUploadingKey(null);
      // Cleared so picking the SAME file again still fires onChange.
      if (fileInputs.current[key]) fileInputs.current[key].value = "";
    }
  };

  const updateCategoryLinks = (categoryIndex, updater) => {
    setData((prev) =>
      (Array.isArray(prev) ? prev : []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        const nextLinks =
          typeof updater === "function"
            ? updater(Array.isArray(category.links) ? category.links : [])
            : updater;
        return {
          ...category,
          links: Array.isArray(nextLinks) ? nextLinks : [],
        };
      })
    );
  };

  const addCategory = () => {
    setData((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const nextIndex = list.length + 1;
      return [
        ...list,
        {
          key: `section-${nextIndex}`,
          title: `New Category ${nextIndex}`,
          links: [],
        },
      ];
    });
  };

  const updateCategory = (categoryIndex, updates) => {
    setData((prev) =>
      (Array.isArray(prev) ? prev : []).map((category, idx) => {
        if (idx !== categoryIndex) return category;
        return { ...category, ...updates };
      })
    );
  };

  const removeCategory = (categoryIndex) => {
    setData((prev) => (Array.isArray(prev) ? prev : []).filter((_, idx) => idx !== categoryIndex));
  };

  const moveCategory = (categoryIndex, direction) => {
    setData((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const targetIndex = categoryIndex + direction;
      if (targetIndex < 0 || targetIndex >= list.length) return list;
      const [item] = list.splice(categoryIndex, 1);
      list.splice(targetIndex, 0, item);
      return list;
    });
  };

  const addLink = (categoryIndex) => {
    updateCategoryLinks(categoryIndex, (links) => [
      ...links,
      { label: "New Link", url: "/", active: true },
    ]);
  };

  const toggleLink = (categoryIndex, linkIndex) => {
    updateCategoryLinks(categoryIndex, (links) =>
      links.map((item, i) => (i === linkIndex ? { ...item, active: !item.active } : item))
    );
  };

  const updateLink = (categoryIndex, linkIndex, field, value) => {
    updateCategoryLinks(categoryIndex, (links) =>
      links.map((item, i) => (i === linkIndex ? { ...item, [field]: value } : item))
    );
  };

  const deleteLink = (categoryIndex, linkIndex) => {
    updateCategoryLinks(categoryIndex, (links) => links.filter((_, i) => i !== linkIndex));
  };

  const moveLink = (categoryIndex, linkIndex, direction) => {
    updateCategoryLinks(categoryIndex, (currentLinks) => {
      const links = [...currentLinks];
        const targetIndex = linkIndex + direction;
      if (targetIndex < 0 || targetIndex >= links.length) return links;
        const [item] = links.splice(linkIndex, 1);
        links.splice(targetIndex, 0, item);
      return links;
    });
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h3>Footer Navigation</h3>
          <p>Manage footer categories and links</p>
        </div>
        <button type="button" className={styles.addBtn} onClick={addCategory}>
          <Plus size={14} /> Add Category
        </button>
      </div>

      {categories.map((category, categoryIndex) => (
        <div key={category.key || categoryIndex} className={styles.group}>
          <div className={styles.groupHeader}>
            <div className={styles.categoryHeading}>
              <input
                className={styles.categoryInput}
                value={category.title || ""}
                onChange={(e) => {
                  const title = e.target.value;
                  updateCategory(categoryIndex, {
                    title,
                    key: slugify(title || category.key || `section-${categoryIndex + 1}`) || `section-${categoryIndex + 1}`,
                  });
                }}
                placeholder="Category title"
              />
            </div>
            <div className={styles.groupActions}>
              <button type="button" className={styles.addBtn} onClick={() => addLink(categoryIndex)}>
                <Plus size={14} /> Add Link
              </button>
              <MoveControl
                onMoveUp={() => moveCategory(categoryIndex, -1)}
                onMoveDown={() => moveCategory(categoryIndex, 1)}
                disableUp={categoryIndex === 0}
                disableDown={categoryIndex === categories.length - 1}
                upTitle="Move category up"
                downTitle="Move category down"
              />
              <button
                type="button"
                className={styles.deleteCategoryBtn}
                onClick={() => removeCategory(categoryIndex)}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>

          <div className={styles.linksArea}>
            {(category.links || []).length === 0 ? (
              <p className={styles.emptyState}>No links yet. Add a link to populate this category.</p>
            ) : (
              (category.links || []).map((item, index) => (
                <div key={index} className={styles.row}>
                  <input
                    className={styles.input}
                    value={item.label}
                    onChange={e => updateLink(categoryIndex, index, "label", e.target.value)}
                    placeholder="Link text (e.g. Customer Service)"
                  />

                  {/* Tidied on blur, not on every keystroke, so typing "//" on
                      the way to "https://" is not fought. The warning is
                      advisory: it names the problem and still lets the page
                      save, since a half-typed link should not block the rest. */}
                  <div className={styles.urlField}>
                    <input
                      className={`${styles.input} ${
                        linkUrlProblem(item.url) ? styles.inputWarn : ""
                      }`}
                      value={item.url}
                      onChange={e => updateLink(categoryIndex, index, "url", e.target.value)}
                      onBlur={e => {
                        const tidy = tidyLinkUrl(e.target.value);
                        if (tidy !== item.url) updateLink(categoryIndex, index, "url", tidy);
                      }}
                      placeholder="Link URL (e.g. /help/customer-service)"
                    />
                    {linkUrlProblem(item.url) && (
                      <span className={styles.urlWarn}>{linkUrlProblem(item.url)}</span>
                    )}

                    {/* Attach a PDF instead of typing a URL. The upload puts the
                        file in storage and drops its address into the field
                        above, so the link behaves like any other from there. */}
                    <div className={styles.docRow}>
                      <input
                        ref={(el) => { fileInputs.current[`${categoryIndex}-${index}`] = el; }}
                        type="file"
                        accept="application/pdf,.pdf"
                        className={styles.fileInput}
                        onChange={(e) => handleDocument(categoryIndex, index, e.target.files?.[0])}
                      />
                      <button
                        type="button"
                        className={styles.docBtn}
                        disabled={uploadingKey === `${categoryIndex}-${index}`}
                        onClick={() => fileInputs.current[`${categoryIndex}-${index}`]?.click()}
                        title="Upload a PDF and link to it"
                      >
                        <FileUp size={13} />
                        {uploadingKey === `${categoryIndex}-${index}`
                          ? "Uploading…"
                          : /\.pdf(\?|#|$)/i.test(item.url || "")
                            ? "Replace PDF"
                            : "Upload PDF"}
                      </button>
                      {/\.pdf(\?|#|$)/i.test(item.url || "") && (
                        <a
                          className={styles.docLink}
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open current PDF
                        </a>
                      )}
                    </div>
                  </div>

                  <div className={styles.rowActions}>
                    <ToggleSwitch
                      checked={item.active}
                      onChange={() => toggleLink(categoryIndex, index)}
                    />
                    <MoveControl
                      onMoveUp={() => moveLink(categoryIndex, index, -1)}
                      onMoveDown={() => moveLink(categoryIndex, index, 1)}
                      disableUp={index === 0}
                      disableDown={index === (category.links || []).length - 1}
                      upTitle="Move link up"
                      downTitle="Move link down"
                    />

                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => deleteLink(categoryIndex, index)}
                      title="Remove link"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FooterNavigation;
